import { el, triggerDownload } from "./dom";
import { iconLock } from "./icons";
import { renderDropzone } from "./dropzone";
import { showToast, showStickyToast, resolveStickyToast } from "./toast";
import type { CryptoWorkerClient } from "../lib/workerClient";
import { saveWrappedKey, loadWrappedKey } from "../keystore";
import { bytesToBlob } from "../lib/bytes";
import { humanizeGeneric } from "../lib/humanizeError";
import { renderSnippetTabs } from "./codeSnippets";

const KEYGEN_SNIPPETS = {
  sdk: `import { generateKeypair, wrapPrivateKey } from "secure-crypt-sdk";

const { publicKey, privateKey } = await generateKeypair();
// Persist privateKey encrypted at rest - never store it raw:
const wrapped = await wrapPrivateKey(privateKey, passphraseBytes);`,
  cli: `python -m cli.main keygen --out-dir ./mykeys
# Passphrase prompted interactively (hidden input), never a CLI argument.
# Writes mykeys/private.key (encrypted) and mykeys/public.key.`,
};

// Module-scoped so the single beforeunload listener below always reflects
// the latest panel instance's state - switching tabs and back shouldn't
// stack duplicate listeners.
//
// Keygen never auto-persists the key by design (a private key shouldn't
// get written down without the user asking), but nothing used to tell the
// user that, so refreshing right after generating - before clicking
// download or save - silently lost the key. This flag plus the
// beforeunload guard and the warning banner are the fix.
let needsBackupWarning = false;

// Losing your only copy of a key is bad enough that this warning needs to
// survive auto-dismiss and even a manual close, so it's a sticky toast:
// closing it just collapses it to a small pinned reminder instead of
// removing it.
const BACKUP_WARNING_KEY = "keygen-backup-warning";

window.addEventListener("beforeunload", (e) => {
  if (needsBackupWarning) {
    e.preventDefault();
    // Browsers ignore custom text here and show their own generic prompt,
    // but returnValue still has to be set to trigger it at all.
    e.returnValue = "";
  }
});

export function renderKeygenPanel(worker: CryptoWorkerClient, onUnlocked: (publicKey: Uint8Array) => void): HTMLElement {
  // The full public key is shown in exactly one place - the right-hand
  // keys card - so this just confirms readiness instead of reprinting it.
  const readyNotice = el("p", { class: "section-sub hidden" }, [
    "Your public key is ready - see it on the right.",
  ]);
  const resultCard = el("div", {}, []);

  function showBackupWarning() {
    needsBackupWarning = true;
    showStickyToast(
      BACKUP_WARNING_KEY,
      "This key exists only in this browser tab right now. Download a backup or save it in this browser below - otherwise it's gone for good if you refresh or close this tab.",
    );
  }
  function clearBackupWarning() {
    needsBackupWarning = false;
    resolveStickyToast(BACKUP_WARNING_KEY);
  }

  const passInput = el("input", { type: "password", autocomplete: "new-password", id: "keygen-pass", placeholder: "Choose a strong passphrase" });
  const confirmInput = el("input", { type: "password", autocomplete: "new-password", id: "keygen-pass-confirm", placeholder: "Type it again" });
  const genBtn = el("button", { type: "button", class: "primary-btn" }, [iconLock(), "Generate new keypair"]);

  genBtn.addEventListener("click", async () => {
    resultCard.innerHTML = "";
    readyNotice.classList.add("hidden");
    clearBackupWarning();
    if (passInput.value.length === 0) {
      showToast("Enter a passphrase first.", "error");
      return;
    }
    if (passInput.value !== confirmInput.value) {
      showToast("Those passphrases don't match. Try again.", "error");
      return;
    }
    const passphrase = new TextEncoder().encode(passInput.value);
    passInput.value = "";
    confirmInput.value = "";
    try {
      const { publicKey, wrapped } = await worker.generateAndWrapKeypair(passphrase);
      showToast("Keypair generated.", "success");
      readyNotice.classList.remove("hidden");

      const blob = bytesToBlob(wrapped);
      // Download survives site data getting cleared, so it's the primary
      // button; "remember in this browser" is just the convenient option.
      const downloadBtn = el("button", { type: "button", class: "primary-btn" }, ["Save an encrypted backup"]);
      downloadBtn.addEventListener("click", () => {
        triggerDownload("private.key", blob);
        clearBackupWarning();
        showToast("Backup downloaded. Keep it somewhere safe - it's still passphrase-protected.", "success");
      });

      const saveBtn = el("button", { type: "button", class: "secondary-btn" }, ["Remember in this browser"]);
      saveBtn.addEventListener("click", async () => {
        try {
          await saveWrappedKey({ id: "default", publicKey, wrapped, createdAt: Date.now() });
          clearBackupWarning();
          showToast("Saved - still passphrase-wrapped - to this browser's local storage.", "success");
        } catch (e) {
          showToast(humanizeGeneric(e, "Couldn't save the key in this browser. Download a backup instead."), "error");
        }
      });

      resultCard.append(el("div", { class: "actions" }, [downloadBtn, saveBtn]));
      showBackupWarning();
      onUnlocked(publicKey);
    } catch (e) {
      showToast(humanizeGeneric(e, "Generating a keypair didn't work."), "error");
    }
  });

  // --- Unlock an existing key (from file or from this browser's storage) ---
  const unlockPassInput = el("input", { type: "password", autocomplete: "current-password", id: "unlock-pass", placeholder: "Its passphrase" });
  const dz = renderDropzone({
    id: "unlock-file",
    accept: ".key",
    emptyTitle: "No key file chosen",
    emptyMeta: "A backup downloaded from this or another session.",
    onFileChange: (f) => {
      if (f) dz.setDisplay(f.name, "Ready to unlock");
      else dz.reset();
    },
  });
  const unlockFromFileBtn = el("button", { type: "button", class: "secondary-btn" }, ["Unlock from file"]);
  const unlockFromStoreBtn = el("button", { type: "button", class: "secondary-btn" }, ["Unlock saved browser key"]);

  async function doUnlock(wrapped: Uint8Array) {
    if (unlockPassInput.value.length === 0) {
      showToast("Enter your passphrase.", "error");
      return;
    }
    const passphrase = new TextEncoder().encode(unlockPassInput.value);
    unlockPassInput.value = "";
    try {
      const { publicKey } = await worker.unlockPrivateKey(wrapped, passphrase);
      readyNotice.classList.remove("hidden");
      clearBackupWarning(); // this key was already persisted somewhere - nothing new to back up
      showToast("Unlocked for this session.", "success");
      onUnlocked(publicKey);
    } catch {
      showToast("That passphrase didn't unlock this key. Try another.", "error");
    }
  }

  unlockFromFileBtn.addEventListener("click", async () => {
    const file = dz.fileInput.files?.[0];
    if (!file) {
      showToast("Choose a key file first.", "error");
      return;
    }
    const wrapped = new Uint8Array(await file.arrayBuffer());
    await doUnlock(wrapped);
  });

  unlockFromStoreBtn.addEventListener("click", async () => {
    const record = await loadWrappedKey("default");
    if (!record) {
      showToast("No key has been saved in this browser yet.", "error");
      return;
    }
    await doUnlock(record.wrapped);
  });

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Keys · Generate"]),
    el("h2", {}, ["Create your identity key"]),
    el("p", { class: "lede" }, [
      "This keypair lets others send you sealed messages only you can open. The private half is encrypted with your passphrase before it's ever written down or saved - we show you the public half only.",
    ]),
    el("p", { class: "section-label" }, ["Integration"]),
    renderSnippetTabs(KEYGEN_SNIPPETS),
    el("p", { class: "section-label" }, ["Try it live"]),
    el("div", { class: "card" }, [
      el("p", { class: "field-label" }, ["Passphrase"]),
      passInput,
      el("p", { class: "field-label" }, ["Confirm passphrase"]),
      confirmInput,
      el("div", { class: "actions" }, [genBtn]),
      readyNotice,
      resultCard,

      el("div", { class: "divider" }, []),
      el("p", { class: "section-label" }, ["Already have a key?"]),
      el("p", { class: "section-sub" }, ["Unlock it for this session - from a backup file, or one saved earlier in this browser."]),
      el("p", { class: "field-label" }, ["Passphrase"]),
      unlockPassInput,
      el("p", { class: "field-label" }, ["Key file"]),
      dz.element,
      el("div", { class: "actions" }, [unlockFromFileBtn, unlockFromStoreBtn]),
    ]),
  ]);
}
